import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper,
    Checkbox, FormControlLabel, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import PercentInput from '../PercentInput';
import RatesVersionSelect from '../RatesVersionSelect';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const PLANS = [
    { id: 'essential', label: 'Essential' },
    { id: 'classic', label: 'Classic' },
    { id: 'classic_with_dental', label: 'Classic with Dental' },
    { id: 'gold', label: 'Gold' },
    { id: 'gold_with_dental', label: 'Gold with Dental' },
    { id: 'gold_superior', label: 'Gold Superior' },
    { id: 'gold_superior_with_dental', label: 'Gold Superior with Dental' },
];

const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly', 'Monthly'];
const BRAND_COLOR = '#00bcd4';
// Same navy as Company_Rates' header and table head
const HEADER_COLOR = '#0c1b4e';
const BRAND_HOVER = '#00acc1';

function getCurrencySign(currency) {
    if (currency === 'USD') return '$';
    if (currency === 'SGD') return 'S$';
    if (currency === 'EURO') return '€';
    if (currency === 'GBP') return '£';
    return '';
}

function formatRate(rate, currency) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return getCurrencySign(currency) + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as Lifeline: discount/loading scale the plan rates only, not the assistance covers
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function Company_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const areaOptions = appConfig?.content?.set2_area_of_coverage || [];
    const defaultArea = areaOptions.includes('Worldwide excl USA') ? 'Worldwide excl USA' : (areaOptions[0] || '');
    const currencyOptions = appConfig?.content?.currencies || [];
    const getDeductibles = (curr) => appConfig?.content?.deductibles?.company?.[curr] || [];

    const companyDiscounts = appConfig?.content?.specifics?.company?.company_discounts || {};
    const groupSizes = appConfig?.content?.specifics?.company?.group_sizes || {};
    const mhdLoadings = appConfig?.content?.specifics?.company?.mhd_loadings || {};
    const companyDiscountOptions = Object.keys(companyDiscounts);
    const groupSizeOptions = Object.keys(groupSizes);
    const mhdLoadingOptions = Object.keys(mhdLoadings);

    const [selectedPlan, setSelectedPlan] = useState('essential');
    const [areaOfCoverage, setAreaOfCoverage] = useState(defaultArea);
    const [currency, setCurrency] = useState('USD');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [includeEvacuation, setIncludeEvacuation] = useState(false);
    const [includeRepatriation, setIncludeRepatriation] = useState(false);
    const [companyDiscount, setCompanyDiscount] = useState(companyDiscountOptions[0] || '');
    const [groupSize, setGroupSize] = useState(groupSizeOptions[0] || '');
    const [mhdLoading, setMhdLoading] = useState(mhdLoadingOptions[0] || '');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [quoteName]: deductible string }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const initializingFromEdit = useRef(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    // Scroll to bottom after new rates are loaded
    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    // Mirrors Company_Rates.js's getDeductibleIndex exactly — loops every currency's
    // deductible list (not just the currently selected one) and returns the index of the
    // first match, so the same deductible label always maps to the same D-index the full
    // Quotation page would send, regardless of which currency happens to be selected here.
    function getDeductibleIndex(deductible) {
        const deductiblesByCurrency = appConfig?.content?.deductibles?.company || {};
        for (const curr in deductiblesByCurrency) {
            const options = deductiblesByCurrency[curr] || [];
            for (let i = 0; i < options.length; i++) {
                if (deductible === options[i]) {
                    return 'D' + i;
                }
            }
        }
        return 'D0';
    }

    // Pre-populate form when opening in edit mode
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            initializingFromEdit.current = true;
            const planId = (editingPlan.plan_id || '').replace('company_', '');
            setSelectedPlan(planId);
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setCurrency(editingPlan.payloads?.currency || 'USD');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            setCompanyDiscount(editingPlan.payloads?.company_discount || companyDiscountOptions[0] || '');
            setGroupSize(editingPlan.payloads?.group_size || groupSizeOptions[0] || '');
            setMhdLoading(editingPlan.payloads?.mhd_loading || mhdLoadingOptions[0] || '');
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || '';
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData from existing quotations
            const ratesMap = {};
            let anyEvacuation = false;
            let anyRepatriation = false;
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const matchesEditingPlan = p => editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id;
                // Evacuation/Repatriation were baked into each client's own premium_rate on save
                const evacuations = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(matchesEditingPlan);
                    return premium?.evacuation_premium ?? null;
                });
                const repatriations = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(matchesEditingPlan);
                    return premium?.repatriation_premium ?? null;
                });
                if (evacuations.some(e => e != null)) anyEvacuation = true;
                if (repatriations.some(r => r != null)) anyRepatriation = true;
                const rates = clients.map((client, ci) => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(matchesEditingPlan);
                    let val = premium?.premium_rate ?? null;
                    // Strip the baked-in evacuation/repatriation amounts so the displayed rate is plan-only
                    if (val != null && evacuations[ci] != null) val -= evacuations[ci];
                    if (val != null && repatriations[ci] != null) val -= repatriations[ci];
                    // Undo the saved discount/loading so ratesData holds the raw rate again
                    if (val != null && savedFactor > 0) val /= savedFactor;
                    return { [planId]: val };
                });
                ratesMap[quote.uid] = { rates, evacuations, repatriations };
            });
            setIncludeEvacuation(anyEvacuation);
            setIncludeRepatriation(anyRepatriation);
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
            setTimeout(() => { initializingFromEdit.current = false; }, 0);
        } else if (open) {
            setSelectedPlan('essential');
            setAreaOfCoverage(defaultArea);
            setCurrency('USD');
            setPaymentFrequency('Annually');
            setIncludeEvacuation(false);
            setIncludeRepatriation(false);
            setCompanyDiscount(companyDiscountOptions[0] || '');
            setGroupSize(groupSizeOptions[0] || '');
            setMhdLoading(mhdLoadingOptions[0] || '');
            setDiscount('0');
            setLoadingPercent('0');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Reset deductibles when currency changes (skip when initializing from edit)
    useEffect(() => {
        if (initializingFromEdit.current) return;
        const defaults = {};
        (quotes || []).forEach(q => {
            defaults[q.name] = getDeductibles(currency)[0] || '';
        });
        setQuoteDeductibles(defaults);
        invalidateRates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency]);

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name] === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (!quoteDeductibles[q.name]) errors[q.name] = true;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const first = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please select a deductible for "${first?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});

        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: {
                                company: {
                                    label: quoteDeductibles[quote.name] || '',
                                    deductible_index: getDeductibleIndex(quoteDeductibles[quote.name]),
                                },
                            },
                            insurers: ['company'],
                            specifics: {
                                company: {
                                    company_discount: companyDiscount,
                                    group_size: groupSize,
                                    mhd_loading: mhdLoading,
                                },
                            },
                            specific_area_of_coverage: { company: areaOfCoverage },
                            rates_version_per_insurer: { company: selectedRatesVersion?.company || '' },
                            specific_currency: { company: currency },
                            specific_payment_frequency: { company: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.company }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                // assistance_covers is index-aligned with rates (one entry per client)
                const evacuations = (data?.assistance_covers || []).map(ac => ac?.evacuation ?? null);
                const repatriations = (data?.assistance_covers || []).map(ac => ac?.repatriation ?? null);
                ratesMap[quoteUid] = { ...data, evacuations, repatriations };
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching BUPA Company rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // ratesData keeps the raw API rates; discount/loading are applied on read
    function getRate(clientIndex, planId, quoteUid) {
        if (!ratesData?.[quoteUid]?.rates) return null;
        const rate = ratesData[quoteUid].rates[clientIndex]?.[planId];
        return typeof rate === 'number' ? rate * rateFactor : rate;
    }

    function getSubtotal(planId, quoteUid) {
        if (!ratesData?.[quoteUid]?.rates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getRate(i, planId, quoteUid);
            if (typeof rate === 'number') {
                total += rate;
                hasAny = true;
            }
        }
        return hasAny ? total : null;
    }

    // Evacuation/Repatriation assistance cover amounts — quoted per client
    function getEvacuation(clientIndex, quoteUid) {
        return ratesData?.[quoteUid]?.evacuations?.[clientIndex] ?? null;
    }

    function getEvacuationSubtotal(quoteUid) {
        if (!ratesData?.[quoteUid]?.evacuations) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const val = ratesData[quoteUid].evacuations[i];
            if (typeof val === 'number') { total += val; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function getRepatriation(clientIndex, quoteUid) {
        return ratesData?.[quoteUid]?.repatriations?.[clientIndex] ?? null;
    }

    function getRepatriationSubtotal(quoteUid) {
        if (!ratesData?.[quoteUid]?.repatriations) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const val = ratesData[quoteUid].repatriations[i];
            if (typeof val === 'number') { total += val; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;
        const selectedPlanObj = PLANS.find(p => p.id === selectedPlan);
        // plan_id identifies the tier and is shared by every instance of it; uid identifies this
        // specific plan instance so multiple plans of the same tier can coexist instead of
        // overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getRate(clientIndex, selectedPlan, quote.uid);
                // Evacuation/Repatriation are quoted per-client — bake each client's own amount into their premium.
                const evacuation = includeEvacuation ? getEvacuation(clientIndex, quote.uid) : null;
                const repatriation = includeRepatriation ? getRepatriation(clientIndex, quote.uid) : null;
                let premiumRate = typeof rate === 'number' ? rate : null;
                const applyEvacuation = premiumRate != null && typeof evacuation === 'number';
                const applyRepatriation = premiumRate != null && typeof repatriation === 'number';
                if (applyEvacuation) premiumRate += evacuation;
                if (applyRepatriation) premiumRate += repatriation;
                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: `company_${selectedPlanObj.id}`,
                    planUid,
                    premium: {
                        insurer: 'company',
                        plan: selectedPlanObj.id,
                        plan_label: selectedPlanObj.label,
                        included_benefits: '',
                        deductible: quoteDeductibles[quote.name] || '',
                        area_of_coverage: areaOfCoverage,
                        currency,
                        payment_frequency: paymentFrequency,
                        company_discount: companyDiscount,
                        group_size: groupSize,
                        mhd_loading: mhdLoading,
                        discount,
                        loading: loadingPercent,
                        premium_rate: premiumRate,
                        evacuation_premium: applyEvacuation ? evacuation : null,
                        repatriation_premium: applyRepatriation ? repatriation : null,
                    },
                });
            });
        });
        const plan = {
            plan_id: `company_${selectedPlan}`,
            uid: planUid,
            plan: selectedPlan,
            insurer: 'company',
            label: `BUPA Company ${selectedPlanObj?.label || selectedPlan}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                currency,
                payment_frequency: paymentFrequency,
                company_discount: companyDiscount,
                group_size: groupSize,
                mhd_loading: mhdLoading,
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || ''])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const deductibleOptions = getDeductibles(currency);
    const activePlans = PLANS.filter(p => p.id === selectedPlan);
    const selectedPlanObj = PLANS.find(p => p.id === selectedPlan);

    // A checked assistance cover with no data for some quote (e.g. checked after reopening
    // in edit mode, before re-running View Rates) would silently save without that cover.
    const missingAssistanceData = !!ratesData && (quotes || []).some(quote => !isBlankQuoteRates(ratesData, quote.uid) && (
        (includeEvacuation && getEvacuationSubtotal(quote.uid) == null) ||
        (includeRepatriation && getRepatriationSubtotal(quote.uid) == null)
    ));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: HEADER_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: '#0369ae',
                    width: 100,
                    height: 60,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 13,
                    lineHeight: 1.2,
                }}>Company</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={`${process.env.PUBLIC_URL}/images/Raffles Health Insurance.png`} alt="Raffles Health Insurance" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <img src={`${process.env.PUBLIC_URL}/images/BUPA Blue Logo.png`} alt="BUPA" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', gap: 6, mb: 2, mt: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {/* First column: Plan Type + Assistance Cover */}
                    <Box>
                        {/* Select Plan */}
                        <Box sx={{ mb: 2 }}>
                            <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                            <FormControl size="small" sx={{ minWidth: 185 }}>
                                <Select
                                    value={selectedPlan}
                                    disabled={loading || !!editingPlan}
                                    // Rates for every plan come back in one fetch, so switching plans just shows a different row
                                    onChange={(e) => setSelectedPlan(e.target.value)}
                                    sx={{ fontSize: 12, height: 32 }}
                                >
                                    {PLANS.map(plan => (
                                        <MenuItem key={plan.id} value={plan.id} sx={{ fontSize: 12 }}>{plan.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Assistance Cover */}
                        <Box>
                            <Typography fontWeight={600} sx={{ mb: 0.5, fontSize: 15 }}>Assistance Cover</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <FormControlLabel
                                    sx={{ mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={includeEvacuation}
                                            disabled={loading}
                                            onChange={(e) => setIncludeEvacuation(e.target.checked)}
                                        />
                                    }
                                    label="Evacuation"
                                />
                                <FormControlLabel
                                    sx={{ mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={includeRepatriation}
                                            disabled={loading}
                                            onChange={(e) => setIncludeRepatriation(e.target.checked)}
                                        />
                                    }
                                    label="Repatriation"
                                />
                            </Box>
                            {missingAssistanceData && (
                                <Typography sx={{ fontSize: 12, color: '#757575', maxWidth: 220 }}>
                                    Click "View Rates" to view the assistance cover premium rates.
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Second column: Options + Company Specifics (label | select | extra) */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 160px auto', columnGap: 3, rowGap: 1.5, alignItems: 'center' }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area of Coverage</Typography>
                        <FormControl size="small" sx={{ width: 160 }}>
                            <Select
                                value={areaOfCoverage}
                                disabled={loading}
                                onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {areaOptions.map(area => (
                                    <MenuItem key={area} value={area} sx={{ fontSize: 12 }}>{area}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Currency</Typography>
                        <FormControl size="small" sx={{ width: 160 }}>
                            <Select
                                value={currency}
                                disabled={loading}
                                onChange={(e) => setCurrency(e.target.value)}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {currencyOptions.map(c => (
                                    <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 160 }}>
                            <Select
                                value={paymentFrequency}
                                disabled={loading}
                                onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {PAYMENT_FREQUENCIES.map(f => (
                                    <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>{f}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box />

                        {companyDiscountOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>Company Discount</Typography>
                                <CustomSelect2
                                    dense
                                    sx={{ width: 160 }}
                                    value={companyDiscount}
                                    onChange={(e) => { setCompanyDiscount(e.target.value); invalidateRates(); }}
                                    items={companyDiscountOptions}
                                    disabled={loading}
                                />
                                <Box />
                            </>
                        )}
                        {groupSizeOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>Group Size</Typography>
                                <CustomSelect2
                                    dense
                                    sx={{ width: 160 }}
                                    value={groupSize}
                                    onChange={(e) => { setGroupSize(e.target.value); invalidateRates(); }}
                                    items={groupSizeOptions}
                                    disabled={loading}
                                />
                                <Box />
                            </>
                        )}
                        {mhdLoadingOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>MHD Loading</Typography>
                                <CustomSelect2
                                    dense
                                    sx={{ width: 160 }}
                                    value={mhdLoading}
                                    onChange={(e) => { setMhdLoading(e.target.value); invalidateRates(); }}
                                    items={mhdLoadingOptions}
                                    disabled={loading}
                                />
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: BRAND_COLOR }}>
                                    {((mhdLoadings[mhdLoading] || 0) * 100)}%
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* Third column: Renewal/Further Discount & Loading */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography fontWeight={600} sx={{ fontSize: 13, width: 170 }}>Renewal/Further Discount</Typography>
                            <PercentInput
                                value={discount}
                                onCommit={setDiscount}
                                disabled={loading}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography fontWeight={600} sx={{ fontSize: 13, width: 170 }}>Renewal/Further Loading</Typography>
                            <PercentInput
                                value={loadingPercent}
                                onCommit={setLoadingPercent}
                                disabled={loading}
                            />
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Per-Quote Deductibles */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductibles per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ minWidth: 200, fontSize: 13, fontWeight: 600 }}>{quote.name}</Typography>
                                <CustomSelect2
                                    dense
                                    value={quoteDeductibles[quote.name] || ''}
                                    onChange={(e) => {
                                        setQuoteDeductibles(prev => ({ ...prev, [quote.name]: e.target.value }));
                                        setDeductibleErrors(prev => ({ ...prev, [quote.name]: false }));
                                        invalidateRates();
                                    }}
                                    items={[LEAVE_BLANK, ...deductibleOptions]}
                                    disabled={loading}
                                    error={!!deductibleErrors[quote.name]}
                                    sx={{ minWidth: 200 }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || !selectedPlan || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#5e35b1' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {/* Rates Display */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: '#673ab7' }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => {
                            const deductible = quoteDeductibles[quote.name];
                            return (
                                <Box key={quote.name} sx={{ mb: 3 }}>
                                    <Typography fontWeight={600} sx={{ mb: 1 }}>
                                        {quote.name}
                                        <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 14 }}>
                                            — {deductible}
                                        </Typography>
                                    </Typography>
                                    {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: HEADER_COLOR }}>
                                                    <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 200 }}>Plan</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                            <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                            <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {activePlans.map(plan => (
                                                    <TableRow key={plan.id} sx={{ backgroundColor: '#fff' }}>
                                                        <TableCell sx={{ fontWeight: 500 }}>{plan.label}</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx}>
                                                                {formatRate(getRate(idx, plan.id, quote.uid), currency)}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: 600 }}>
                                                            {formatRate(getSubtotal(plan.id, quote.uid), currency)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {/* Evacuation/Repatriation assistance covers — quoted per client */}
                                                {includeEvacuation && getEvacuationSubtotal(quote.uid) != null && (
                                                    <TableRow sx={{ backgroundColor: '#fff' }}>
                                                        <TableCell>Evacuation</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx}>
                                                                {formatRate(getEvacuation(idx, quote.uid), currency)}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: 500 }}>
                                                            {formatRate(getEvacuationSubtotal(quote.uid), currency)}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {includeRepatriation && getRepatriationSubtotal(quote.uid) != null && (
                                                    <TableRow sx={{ backgroundColor: '#fff' }}>
                                                        <TableCell>Repatriation</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx}>
                                                                {formatRate(getRepatriation(idx, quote.uid), currency)}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: 500 }}>
                                                            {formatRate(getRepatriationSubtotal(quote.uid), currency)}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {((includeEvacuation && getEvacuationSubtotal(quote.uid) != null) ||
                                                  (includeRepatriation && getRepatriationSubtotal(quote.uid) != null)) && (
                                                    <TableRow sx={{ backgroundColor: '#e8eaf6' }}>
                                                        <TableCell sx={{ fontWeight: 700 }}>
                                                            {[
                                                                selectedPlanObj?.label,
                                                                includeEvacuation && getEvacuationSubtotal(quote.uid) != null ? 'Evacuation' : null,
                                                                includeRepatriation && getRepatriationSubtotal(quote.uid) != null ? 'Repatriation' : null,
                                                            ].filter(Boolean).join(' + ')}
                                                        </TableCell>
                                                        {clients.map((client, idx) => {
                                                            const rate = getRate(idx, selectedPlan, quote.uid);
                                                            const evacuation = includeEvacuation ? getEvacuation(idx, quote.uid) : null;
                                                            const repatriation = includeRepatriation ? getRepatriation(idx, quote.uid) : null;
                                                            let total = typeof rate === 'number' ? rate : null;
                                                            if (total != null && typeof evacuation === 'number') total += evacuation;
                                                            if (total != null && typeof repatriation === 'number') total += repatriation;
                                                            return (
                                                                <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                                    {formatRate(total, currency)}
                                                                </TableCell>
                                                            );
                                                        })}
                                                        <TableCell sx={{ fontWeight: 700 }}>
                                                            {formatRate(
                                                                getSubtotal(selectedPlan, quote.uid) != null
                                                                    ? getSubtotal(selectedPlan, quote.uid)
                                                                        + (includeEvacuation ? (getEvacuationSubtotal(quote.uid) || 0) : 0)
                                                                        + (includeRepatriation ? (getRepatriationSubtotal(quote.uid) || 0) : 0)
                                                                    : null,
                                                                currency
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </Paper>
)}
                                </Box>
                            );
                        })}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="company" />

                <RatesVersionSelect
                    insurer="company"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading || missingAssistanceData}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#5e35b1' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
